/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import {
    NotFoundPage,
    Spinner,
    useAssetImages,
    Icon,
    Stack,
    Button,
    Heading,
    FlexRow,
    FlexCol,
    ContentBlock,
    Box,
    Divider,
    DisplayHtmlContent,
    Spacer,
    useFlags,
    useWindowSize,
    useSlugLookup,
    announce,
    SecurityHelper,
    toTitleCase,
    SkipLink,
} from 'sarsaparilla';

import { DonationPromo } from 'shared-ui/components/Donations/DonationPromo';
import { FacilityAlertBannerContainer } from 'shared-ui/containers/FacilityAlertBannerContainer';
import { useSendGAEvent } from 'shared-ui/utils/gaCommon';
import MapCTAWrapper from 'shared-ui/maps/components/MapCTAWrapper';

import { GatewayV1Hero } from 'site-kit';

import { fetchGateway as campingFetchGatewayAction } from 'ui-camping/src/shared/actions/gateway';

import { updateMapSearchResultLocation } from '../redux/map';
import { fetchGateway as inventoryFetchGateway } from '../redux/actions/fetchGateway';

import NearbyActivities from '../components/NearbyActivities';
import CampingDayUse from './CampingDayUse';
import MapContainer from './MapContainer';
import ActivitiesExperiences from './ActivitiesExperiences';
import {
    fetchGatewaySearch as fetchGatewaySearchAction,
    fetchPOISearch as fetchPOISearchAction,
    fetchTicketSearch as fetchTicketSearchAction,
    fetchActivityPassesSearch as fetchActivityPassesSearchAction,
    fetchTreePermitsSearch as fetchTreePermitsSearchAction,
    fetchVenueReservationsSearch as fetchVenueReservationsSearchAction,
    fetchVenueFacilitiesSearch as fetchVenueFacilitiesSearchAction,
} from '../redux/actions/fetchSearch';
import {
    fetchGatewaySearchSelector,
    fetchTicketSearchSelector,
    fetchPOISearchSelector,
    fetchCampingSearchSelector,
    fetchTreePermitsSearchSelector,
    fetchActivityPassesSearchSelector,
    fetchVenueFacilitySelector,
} from '../redux/reducers/fetchSearch';
import GettingHere from '../components/GettingHere';
import WhiteAndGrayBoxes from '../components/WhiteAndGrayBoxes';
import PassPromo from '../components/PassPromo';

const MIN_WIDTH_FOR_ANCHOR_HEADER = 910;

function GatewayPage(props) {
    const {
        gateway,
        campingFetchGateway,
        gatewaySearch,
        gatewayTicketSearch,
        gatewayActivityPassSearch,
        gatewayTreePermitsSearch,
        gatewayPOISearch,
        gatewayCampingSearch,
        gatewayVenueFacilitySearch,
        fetchGatewaySearch,
        fetchActivityPassesSearch,
        fetchTreePermitsSearch,
        fetchTicketSearch,
        fetchVenueReservationsSearch,
        fetchVenueFacilitiesSearch,
        fetchPOISearch,
    } = props;

    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const slugLocation = useSlugLookup(params.recId);
    const locationId = slugLocation?.locationId || params.recId;
    const wasGaEventFired = useRef(false);

    const { gatewayDataFromInventoryService } = useFlags();
    const [gatewayInfo, setGatewayInfo] = useState({ isValid: false });
    const isWidget = SecurityHelper.isWidgetSession();

    useEffect(() => {
        if (location.pathname.includes('/camping')) {
            navigate(`/gateways/${locationId}`);
        }
    }, [location.pathname, locationId, navigate]);

    useEffect(() => {
        if (!gatewayDataFromInventoryService) {
            setGatewayInfo({
                ...gateway?.results,
                latitude: gateway?.results?.recarea_latitude,
                longitude: gateway?.results?.recarea_longitude,
                directions: gateway?.results?.recarea_directions,
                name: gateway?.results?.recarea_name,
                isValid: gateway?.isValid,
            });
        }
    }, [gateway, gatewayDataFromInventoryService]);

    useEffect(
        () => {
            if (slugLocation.loaded && gatewayDataFromInventoryService !== undefined) {
                if (gatewayDataFromInventoryService) {
                    inventoryFetchGateway(locationId).then((data) => {
                        setGatewayInfo(data?.results);
                    });
                } else {
                    campingFetchGateway(locationId);
                }
                fetchActivityPassesSearch({ locationId });
                fetchTreePermitsSearch({ locationId });
                fetchGatewaySearch({ locationId });
                fetchTicketSearch({ locationId });
                fetchVenueFacilitiesSearch({ locationId }).then(({ results }) => {
                    if (results?.length > 0) {
                        const parentVenue = results.find(
                            (item) => item.entity_type === 'venuereservations'
                        );
                        if (parentVenue) {
                            fetchVenueReservationsSearch({
                                locationId: parentVenue?.entity_id,
                            });
                        }
                    }
                });
                fetchPOISearch({ locationId });
            }
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locationId, slugLocation.loaded, gatewayDataFromInventoryService]
    );

    useEffect(
        () => {
            props.updateMap({
                latitude: gatewayInfo.latitude,
                longitude: gatewayInfo.longitude,
                zoomLevel: 10,
            });
        },
        // Old code so declaring lint bankruptcy
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gatewayInfo]
    );

    const { width: windowWidth } = useWindowSize();

    const { heroImage, galleryImages } = useAssetImages({ assetId: locationId });
    let imageUrl = heroImage?.url;
    if (!imageUrl && galleryImages?.length > 0) {
        imageUrl = galleryImages[0].url;
    }

    const locationState = get(gatewaySearch, 'data.results.0.state_code', '');
    const parkPassFacilityId = get(gatewaySearch, 'data.results.0.park_pass_facility_id');
    const parkPassFacilityName = get(
        gatewaySearch,
        'data.results.0.park_pass_facility_name'
    );
    const isNPS = gatewayInfo?.org_code === 'NPS';
    const totalActivities =
        (gatewayTicketSearch?.data?.size || 0) +
        (gatewayActivityPassSearch?.data?.size || 0) +
        (gatewayTreePermitsSearch?.data?.size || 0) +
        (gatewayVenueFacilitySearch?.data?.size || 0);
    //Striping out img tags.
    const htmlContent = gatewayInfo?.description?.replace(
        /<img[^>"']*((("[^"]*")|('[^']*'))[^"'>]*)*>/g,
        ''
    );

    // Fire a GA Event to pass the additional info about this page to the GA dataLayer
    const hasLoaded = slugLocation.loaded && gatewayInfo.isValid;
    const agencyName = gatewayInfo?.agency_name;
    const facilityId = gatewayInfo?.asset_id;
    const facilityName = gatewayInfo?.name;

    // Fire the view_item GA Event
    useSendGAEvent({ itemLevel: 'Gateway', facilityId });

    useEffect(() => {
        if (
            window.dataLayer &&
            !wasGaEventFired.current &&
            hasLoaded &&
            agencyName &&
            facilityId &&
            facilityName &&
            locationState
        ) {
            window.dataLayer.push({
                agencyName,
                facilityId,
                facilityName,
            });

            wasGaEventFired.current = true;
        }
    }, [agencyName, facilityId, facilityName, hasLoaded, locationState]);

    const scrollTo = (id, nameToAnnounce = `Scrolling to ${id} selector on page`) => {
        announce(nameToAnnounce);
        return document
            .getElementById(id)
            .scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const showHeaderLinks = windowWidth > MIN_WIDTH_FOR_ANCHOR_HEADER;
    const headerLinkCount =
        [
            !!gatewayCampingSearch?.data?.size,
            !!totalActivities,
            !!parkPassFacilityId && isNPS,
        ].filter(Boolean).length + 2;

    if (slugLocation.loaded && gatewayInfo.isValid) {
        const officialSiteUrl = gatewayInfo.links?.find(
            ({ link_type }) => link_type === 'Official Web Site'
        )?.url;

        return (
            <div id="page-body">
                <div className="rec-gateway-new" id="rec-gateway-new-id">
                    {!isWidget && (
                        <GatewayV1Hero
                            assetId={locationId}
                            totalCampgrounds={gatewayCampingSearch?.data?.size}
                            totalPOI={gatewayPOISearch?.data?.size}
                            totalActivities={totalActivities}
                            partnerAgency={gatewayInfo.org_code}
                            locationState={locationState}
                            inventoryListButtons={!showHeaderLinks}
                            onCampgroundsClick={() =>
                                scrollTo('camping-day-use-wrapper-id')
                            }
                            onActivitiesClick={() => scrollTo('activities-wrapper-id')}
                            onPOIClick={() => scrollTo('poi-wrapper-id')}
                            officialUrl={officialSiteUrl}
                            title={gatewayInfo.name}
                            favoriteEntityId={locationId}
                            favoriteEntityType={'recarea'}
                            isInventoryCountIgnored
                        />
                    )}
                    <FacilityAlertBannerContainer />
                    <ContentBlock>
                        <Stack>
                            <Spacer size="xs" />
                            {showHeaderLinks && (
                                <div>
                                    <Box>
                                        <FlexRow
                                            alignItems="center"
                                            justifyContent={
                                                headerLinkCount > 2
                                                    ? 'space-between'
                                                    : 'space-around'
                                            }
                                            className="gateway-helper-links"
                                        >
                                            <FlexCol md="variable">
                                                <Button
                                                    appearance="link"
                                                    gaTrackingId="566179109993"
                                                    onClick={() =>
                                                        scrollTo(
                                                            'gateway-map-wrapper-id',
                                                            'Scrolling to map'
                                                        )
                                                    }
                                                >
                                                    EXPLORE THE MAP
                                                </Button>
                                            </FlexCol>
                                            {!!gatewayCampingSearch?.data?.size && (
                                                <FlexCol md="variable">
                                                    <Button
                                                        appearance="link"
                                                        gaTrackingId="418119126028"
                                                        onClick={() =>
                                                            scrollTo(
                                                                'camping-day-use-wrapper-id',
                                                                'Scrolling to camping and day use section'
                                                            )
                                                        }
                                                    >
                                                        CAMPING & DAY USE
                                                    </Button>
                                                </FlexCol>
                                            )}
                                            {!!totalActivities && (
                                                <FlexCol md="variable">
                                                    <Button
                                                        appearance="link"
                                                        gaTrackingId="238120876563"
                                                        onClick={() =>
                                                            scrollTo(
                                                                'activities-wrapper-id',
                                                                'Scrolling to activities and experiences section'
                                                            )
                                                        }
                                                    >
                                                        ACTIVITIES & EXPERIENCES
                                                    </Button>
                                                </FlexCol>
                                            )}
                                            <FlexCol md="variable">
                                                <Button
                                                    appearance="link"
                                                    gaTrackingId="467866638406"
                                                    onClick={() =>
                                                        scrollTo(
                                                            'directions-wrapper-id',
                                                            'Scrolling to Driving Directions'
                                                        )
                                                    }
                                                >
                                                    DIRECTIONS
                                                </Button>
                                            </FlexCol>
                                            {!!parkPassFacilityId && isNPS && (
                                                <FlexCol md="variable">
                                                    <Button
                                                        appearance="tertiary"
                                                        gaTrackingId="310540700206"
                                                        iconBeforeElement={
                                                            <Icon
                                                                iconName="card"
                                                                size="md"
                                                            />
                                                        }
                                                        onClick={() =>
                                                            scrollTo(
                                                                'pass-promo-wrapper-id',
                                                                'Scrolling to Park Pass section'
                                                            )
                                                        }
                                                    >
                                                        Entrance Fee Required
                                                    </Button>
                                                </FlexCol>
                                            )}
                                        </FlexRow>
                                    </Box>
                                    <Divider />
                                </div>
                            )}
                            <Box>
                                <FlexRow>
                                    <FlexCol md={12}>
                                        <SkipLink
                                            position="above"
                                            href="#skip-to-buy-a-pass"
                                        >
                                            Skip to Buy a Pass
                                        </SkipLink>
                                        <Heading headingLevel={2} appearance="h3">
                                            Find your next adventure
                                        </Heading>
                                        <Spacer size="sm" />
                                        <DisplayHtmlContent html={htmlContent} />
                                    </FlexCol>
                                </FlexRow>
                            </Box>
                            <Spacer size="gutter" />
                        </Stack>
                    </ContentBlock>

                    <ContentBlock>
                        <div
                            className="gateway-map-container"
                            id="gateway-map-wrapper-id"
                        >
                            <MapCTAWrapper
                                message={`Click below to view a map of ${toTitleCase(
                                    gatewayInfo.name
                                )}`}
                                forceDisplayMap={!isWidget}
                            >
                                <MapContainer gateway={gatewayInfo} />
                            </MapCTAWrapper>
                        </div>
                    </ContentBlock>

                    <WhiteAndGrayBoxes>
                        {/* The pass and donation promos are grouped together so they do not interrupt the alternating blue box backgrounds */}
                        <div id="skip-to-buy-a-pass">
                            {parkPassFacilityId ? (
                                <PassPromo
                                    agencyCode={gatewayInfo.org_code}
                                    locationName={gatewayInfo.name}
                                    parkPassFacilityID={parkPassFacilityId}
                                    parkPassFacilityName={parkPassFacilityName}
                                    imageUrl={imageUrl}
                                />
                            ) : null}

                            {/* Test DonationPromo at /camping/gateways/1102 */}
                            <DonationPromo
                                facilityID={locationId}
                                className="my-6"
                                modalSource="gateway-promo"
                            />
                        </div>

                        <CampingDayUse
                            location={{ locationId, name: gatewayInfo.name }}
                        />
                        <ActivitiesExperiences
                            location={{ locationId, name: gatewayInfo.name }}
                        />
                        {!!gatewayPOISearch?.data?.size && (
                            <Box paddingY="xl" id="poi-wrapper-id">
                                <Heading headingLevel={3} appearance="h4">
                                    Points of Interest
                                </Heading>
                                <Spacer size="gutter" />
                                <ul className="three">
                                    {gatewayPOISearch.data.results.map((result) => (
                                        <li key={result.id}>
                                            <a
                                                href={`/camping/poi/${result.entity_id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label={`${result.name} (opens in a new tab)`}
                                            >
                                                {result.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </Box>
                        )}
                        <Box paddingY="xl" id="directions-wrapper-id">
                            <NearbyActivities activities={gatewayInfo.activities} />
                            <Spacer size="gutter" />
                            <Divider />
                            <GettingHere
                                latitude={gatewayInfo.latitude}
                                longitude={gatewayInfo.longitude}
                                drivingDirection={gatewayInfo.directions}
                            />
                        </Box>
                    </WhiteAndGrayBoxes>
                </div>
            </div>
        );
    }
    if (slugLocation.loaded && !isNil(gatewayInfo.error)) {
        return (
            <NotFoundPage
                errorBodyText={`Whoops! We could not find the page you've requested. Please click the button below to go back to the previous page or enter a new search above.`}
            />
        );
    }
    return (
        <div id="page-body">
            <Spinner size="xl" />
        </div>
    );
}

const propTypes = {
    fetchGatewaySearch: PropTypes.func,
    fetchPOISearch: PropTypes.func,
    fetchActivityPassesSearch: PropTypes.func,
    fetchTreePermitsSearch: PropTypes.func,
    fetchTicketSearch: PropTypes.func,
    campingFetchGateway: PropTypes.func.isRequired,
    gateway: PropTypes.any,
    gatewaySearch: PropTypes.any,
    gatewayTicketSearch: PropTypes.any,
    gatewayActivityPassSearch: PropTypes.any,
    gatewayTreePermitsSearch: PropTypes.any,
    gatewayPOISearch: PropTypes.any,
    gatewayCampingSearch: PropTypes.any,
    gatewayVenueFacilitySearch: PropTypes.any,
    fetchVenueReservationsSearch: PropTypes.any,
    fetchVenueFacilitiesSearch: PropTypes.any,
    updateMap: PropTypes.any,
};

function mapStateToProps(state) {
    return {
        search: state.search,
        recarea: state.recarea,
        gateway: state.gateway.gateway,
        gatewaySearch: fetchGatewaySearchSelector(state),
        gatewayTicketSearch: fetchTicketSearchSelector(state),
        gatewayPOISearch: fetchPOISearchSelector(state),
        gatewayCampingSearch: fetchCampingSearchSelector(state),
        gatewayTreePermitsSearch: fetchTreePermitsSearchSelector(state),
        gatewayActivityPassSearch: fetchActivityPassesSearchSelector(state),
        gatewayVenueFacilitySearch: fetchVenueFacilitySelector(state),
    };
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators(
            {
                campingFetchGateway: campingFetchGatewayAction,
                updateMap: updateMapSearchResultLocation,
                fetchGatewaySearch: fetchGatewaySearchAction,
                fetchTicketSearch: fetchTicketSearchAction,
                fetchActivityPassesSearch: fetchActivityPassesSearchAction,
                fetchTreePermitsSearch: fetchTreePermitsSearchAction,
                fetchVenueReservationsSearch: fetchVenueReservationsSearchAction,
                fetchVenueFacilitiesSearch: fetchVenueFacilitiesSearchAction,
                fetchPOISearch: fetchPOISearchAction,
            },
            dispatch
        ),
    };
}

GatewayPage.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(GatewayPage);

// cSpell:ignore recarea, venuereservations
